import { Box, Collapse, Divider, IconButton, Paper } from "@mui/material"
import { AddOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import { type FC, Fragment } from "react"
import { useMemo, useState } from "react"
import { useFieldArray, useFormContext } from "react-hook-form"
import { useShallow } from "zustand/shallow"

import type { SoftReceivedPurchaseOrdersFormValues } from "../../schemas"
import {
  calculateSoftReceivedPurchaseOrderFormTotals,
  getSoftReceivedPalletRowFormEmptyValues,
  resolveSoftReceivedPurchaseOrderEstimatedTotals,
} from "../../utils"
import { GoodsReceiptDetailsSubtotals, GoodsReceiptProductLayout } from "../index"

import { SoftReceivedPalletRowForm } from "./SoftReceivedPalletRowForm"

import { Button } from "@/components/common/Button"
import { CountChip } from "@/components/common/CountChip"
import { PermissionActionType } from "@/constants/permissions"
import type { GoodsReceiptProductDetail, OrderLine } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { formatNumber } from "@/utils/number"

interface SoftReceivedPurchaseOrderLineFormProps {
  purchaseOrderLine: OrderLine
  goodsReceiptLines: GoodsReceiptProductDetail[]
  purchaseOrderIndex: number
  purchaseOrderLineIndex: number
  isResettingRef?: React.MutableRefObject<boolean>
  isSubtotalsVisible?: boolean
}

export const SoftReceivedPurchaseOrderLineForm: FC<SoftReceivedPurchaseOrderLineFormProps> = ({
  purchaseOrderLine,
  goodsReceiptLines,
  purchaseOrderIndex,
  purchaseOrderLineIndex,
  isResettingRef,
  isSubtotalsVisible = true,
}) => {
  const { hasPermissions } = usePermissionManager()
  const [isExpanded, setIsExpanded] = useState(false)
  const { control, watch } = useFormContext<SoftReceivedPurchaseOrdersFormValues>()
  const uoms = useUnitOfMeasurementsStore(useShallow((s) => s.units))

  const canMaintain = hasPermissions([PermissionActionType.WAREHOUSE_RECEIVING_MAINTENANCE])

  const baseFieldPath = `purchaseOrders.${purchaseOrderIndex}.lines.${purchaseOrderLineIndex}` as const
  const fieldPaths = {
    uomId: `${baseFieldPath}.uomId` as const,
    palletRows: `${baseFieldPath}.palletRows` as const,
  }

  const {
    fields: palletRowFields,
    append: appendPalletRow,
    remove: removePalletRow,
  } = useFieldArray({
    control,
    name: fieldPaths.palletRows,
  })

  const product = purchaseOrderLine.product!
  const formValues = watch()
  const uomId = watch(fieldPaths.uomId)
  const uom = uoms.find((uom) => uom.id === uomId)
  const estimatedTotals = resolveSoftReceivedPurchaseOrderEstimatedTotals([purchaseOrderLine], goodsReceiptLines)

  const totals = useMemo(() => {
    const lineFormValues = formValues.purchaseOrders[purchaseOrderIndex]?.lines[purchaseOrderLineIndex]
    if (lineFormValues) {
      return calculateSoftReceivedPurchaseOrderFormTotals([lineFormValues])
    }

    return { palletsCount: 0, unitsCount: 0, quantity: 0, uomId: "" }
  }, [formValues, purchaseOrderIndex, purchaseOrderLineIndex])

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  const handleAddPalletRow = () => {
    appendPalletRow(getSoftReceivedPalletRowFormEmptyValues(product))
  }

  const handleRemovePalletRow = (palletIndex: number) => {
    removePalletRow(palletIndex)
  }

  return (
    <Paper elevation={0} className='rounded-md' data-testid='purchase-order-line'>
      <GoodsReceiptProductLayout product={product}>
        <Box className='flex flex-row flex-wrap items-center justify-end gap-2'>
          <CountChip
            label='Est. Pallets'
            value={String(estimatedTotals.palletsCount)}
            className='h-5'
            data-testid='pallets-count'
          />
          <CountChip
            label='Est. Units'
            value={String(estimatedTotals.unitsCount)}
            className='h-5'
            data-testid='units-count'
          />
          <CountChip
            label='Est. Total UoM'
            value={`${formatNumber(estimatedTotals.quantity ?? 0)} ${uom?.name ?? ""}`}
            className='h-5'
            data-testid='total-uom-count'
          />
        </Box>
        <IconButton size='small' onClick={handleToggleExpand} data-testid='expand-product-details'>
          {isExpanded ? (
            <KeyboardArrowUpOutlined className='text-gray-700' />
          ) : (
            <KeyboardArrowDownOutlined className='text-gray-700' />
          )}
        </IconButton>
      </GoodsReceiptProductLayout>
      <Collapse in={isExpanded}>
        <Box className='flex flex-col gap-y-4 p-4'>
          {palletRowFields.map((palletRowField, palletRowIndex) => (
            <Fragment key={palletRowField.id}>
              <SoftReceivedPalletRowForm
                purchaseOrderIndex={purchaseOrderIndex}
                purchaseOrderLineIndex={purchaseOrderLineIndex}
                palletRowIndex={palletRowIndex}
                uom={uom}
                product={product}
                isResettingRef={isResettingRef}
                onRemove={() => handleRemovePalletRow(palletRowIndex)}
              />
              {palletRowFields.length > 0 && <Divider />}
            </Fragment>
          ))}
          {canMaintain && (
            <Box className='my-4 flex justify-end'>
              <Button
                color='primary'
                size='small'
                startIcon={<AddOutlined />}
                onClick={handleAddPalletRow}
                data-testid='add-pallet-row-button'
              >
                Pallet Row
              </Button>
            </Box>
          )}
          {isSubtotalsVisible && (
            <GoodsReceiptDetailsSubtotals label='Subtotal Received' totals={totals} data-testid='subtotal-received' />
          )}
        </Box>
      </Collapse>
    </Paper>
  )
}
